/**
 * Shared react-select styling for the dark interface.
 *
 * These styles were duplicated in every page, and the copies drifted: the
 * Supervisors page ended up with a white menu, dark grey option text and a blue
 * accent, against the dark purple menu and red accent used everywhere else.
 *
 * Two details matter beyond colour:
 *
 * - `minHeight: 44px` on the control keeps the dropdowns level with the plain
 *   `.filter-input` text fields, which are `height: 44px` in the page CSS.
 * - `menuPortal` must set a z-index. The pages render menus with
 *   `menuPortalTarget={document.body}` to escape the filter panel's overflow, and
 *   react-select's portal container defaults to a z-index low enough for the
 *   table section — positioned, with its own z-index — to paint over the menu.
 */
export const darkSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    background: "rgba(255, 255, 255, 0.1)",
    border: `1px solid ${
      state.isFocused ? "rgba(227, 6, 19, 0.5)" : "rgba(255, 255, 255, 0.2)"
    }`,
    borderRadius: "var(--border-radius-lg)",
    color: "white",
    fontSize: "0.9rem",
    minHeight: "44px",
    backdropFilter: "blur(10px)",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(227, 6, 19, 0.2)" : "none",
    "&:hover": {
      border: "1px solid rgba(227, 6, 19, 0.3)",
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    background: "rgb(26, 20, 51)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "var(--border-radius-lg)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(20px)",
    zIndex: 99999,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 99999,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    background: state.isSelected
      ? "rgba(227, 6, 19, 0.8)"
      : state.isFocused
        ? "rgba(255, 255, 255, 0.1)"
        : "transparent",
    color: "white",
    fontSize: "0.9rem",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "white",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "rgba(255, 255, 255, 0.5)",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: "rgba(255, 255, 255, 0.7)",
    "&:hover": {
      color: "white",
    },
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    color: "rgba(255, 255, 255, 0.7)",
    "&:hover": {
      color: "white",
    },
  }),
  input: (provided: any) => ({
    ...provided,
    color: "white",
  }),
};

/**
 * Variant for selects inside a form — a modal or a page, anywhere `.form-group` is.
 *
 * Light, unlike the filter-bar version: form fields are white when editable and
 * dark grey when read-only, so which is which needs no explanation. A dark select
 * next to white inputs would read as disabled — the opposite of the truth.
 *
 * Sizes match the `.form-group input` rule in index.css: 1rem text, `md` radius.
 */
export const formSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    background: "#ffffff",
    border: `1px solid ${
      state.isFocused ? "rgba(227, 6, 19, 0.6)" : "rgba(255, 255, 255, 0.25)"
    }`,
    borderRadius: "var(--border-radius-md)",
    color: "#1f2937",
    fontSize: "1rem",
    minHeight: "48px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(227, 6, 19, 0.25)" : "none",
    "&:hover": {
      borderColor: "rgba(227, 6, 19, 0.4)",
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "var(--border-radius-md)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",
    zIndex: 99999,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 99999,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    background: state.isSelected
      ? "rgba(227, 6, 19, 0.9)"
      : state.isFocused
        ? "rgba(227, 6, 19, 0.1)"
        : "transparent",
    color: state.isSelected ? "#ffffff" : "#1f2937",
    fontSize: "1rem",
    "&:active": {
      background: "rgba(227, 6, 19, 0.2)",
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
  multiValue: (provided: any) => ({
    ...provided,
    background: "rgba(227, 6, 19, 0.12)",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    // Matches the native placeholder in index.css: paler than a real value.
    color: "#9ca3af",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: "#6b7280",
    "&:hover": {
      color: "#1f2937",
    },
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    color: "#6b7280",
    "&:hover": {
      color: "#1f2937",
    },
  }),
  input: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
};
